import {
    AfterContentInit,
    Component,
    ContentChild,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
} from "@angular/core";
import { BooleanAttribute } from "@hex/angular/decorators";
import { Subject ,  Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { GenericActions } from "../../redux/generic-actions";
import { ImmutableRedux } from "../../redux/immutable-redux.service";
import { ReduxRequestsMap } from "../../redux/redux-requests-map.service";
import { NS_UIState } from "../../types";
import { commonStatefulContainerReducerFactory } from "./reducer";
import { STATEFUL_CONTAINER_STATE_PROP } from "./state-props";

import { isEmpty, isEqual } from "lodash";

@Component({
    selector: "stateful-container",
    templateUrl: "./stateful-container.component.html",
    exportAs: "StatefulContainer",
})
export class StatefulContainerComponent implements OnInit, OnDestroy, AfterContentInit {
    @ContentChild(TemplateRef, {static: false}) public templateRef: TemplateRef<any>;
    @Input() public stateProp: string;
    @Input() @BooleanAttribute() public useExisting = false;
    @Input() @BooleanAttribute() public keepStateAliveOnDestroy = false;
    @Input() @BooleanAttribute() public customEmptyState = false;
    @Input() public valuePath = "value";

    @Input("rootProp") public set rootPropInput(value: string) {
        this.rootProp = value;
    };

    @Input() public initialState: NS_UIState.IStatefulContainerState = {
        value: {},
    };

    public detailId: any;
    @Input("detailId")
    public set detailIdInput(value: any) {
        if (value !== this.detailId) {
            this.detailId = value;

            if (this.useExisting === false || !this.dataExists) {
                this.getDetail();
            }
        }
    }

    public data$: Subscription;
    public refresher$ = new Subject();
    public value: any;
    public initialized = false;

    constructor(
        private reduxRequests: ReduxRequestsMap,
        private ngRedux: ImmutableRedux<any>,
        @Inject(STATEFUL_CONTAINER_STATE_PROP) public rootProp,
    ) { }

    public ngOnInit(): void {
        if (this.useExisting === false || !this.dataExists) {
            this.ngRedux.registerComponent(
                commonStatefulContainerReducerFactory,
                this.rootProp,
                this.stateProp,
                this.initialState,
            );
        }

        this.refresher$.subscribe(() => {
            this.getDetail();
        });
    }

    public ngAfterContentInit(): void {
        this.data$ = this.ngRedux.select(this.rootProp, this.stateProp).subscribe((state: any) => {
            this.initialized = true;
            if (!isEqual(this.value, state[this.valuePath])) {
                this.value = state[this.valuePath];
            }
        });
    }

    public ngOnDestroy() {
        if (!!this.data$) {
            this.data$.unsubscribe();
        }

        if (this.useExisting === false && !this.keepStateAliveOnDestroy) {
            this.resetState();
        }
    }

    public resetState() {
        this.ngRedux.dispatchFactory(GenericActions.STATE_RESET, this.rootProp, this.stateProp);
    }

    public getDetail(): void {
        if (this.detailId !== undefined) {
            this.reduxRequests.within(this.stateProp).getDetail({ id: this.detailId }).pipe(
                map((body: any) => body.data),
            )
            .subscribe((payload) => {
                this.ngRedux.dispatchFactory(
                    GenericActions.DETAIL_LOADED,
                    this.rootProp,
                    this.stateProp,
                    payload,
                );
            });
        }
    }

    public get canDisplay() {
        return !isEmpty(this.value);
    }

    public get dataExists() {
        return this.ngRedux.checkState(this.rootProp, this.stateProp);
    }
}
